import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useAppDispatch } from "../../../app";
import { changeEditing } from "../../../app/store/appSlice";
import { GroupBlockEntityType } from "../../../configMap";
import useAnnotations from "../../../annotation/hooks/useAnnotations";
import useKeyEvent from "../../../configMap/hooks/useKeyEvent";
import { Keys } from "../../constants";

const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

interface UseEditEntityDateProps {
  entityType: GroupBlockEntityType;
  documentId: string;
  annotationId: string;
  defaultValue: Dayjs | null;
  groupBlockIndex: number;
  format: string | null;
  isTableMode: boolean;
}

const useEditEntityDate = ({
  entityType,
  documentId,
  annotationId,
  defaultValue,
  groupBlockIndex,
  format,
  isTableMode,
}: UseEditEntityDateProps) => {
  const dispatch = useAppDispatch();

  const dateFormat = format ?? DEFAULT_DATE_FORMAT;

  const isCancelled = useRef(false);
  const [value, setValue] = useState<Dayjs>(
    defaultValue?.isValid() ? defaultValue : dayjs(),
  );

  const { updateAnnotation } = useAnnotations({
    documentId,
    groupBlockIndex,
  });

  const handleKeyEvent = useKeyEvent(Keys.ESCAPE, () => {
    isCancelled.current = true;
    dispatch(changeEditing(null));
  });

  const handleChange = useCallback(
    (date: Dayjs | null): void => {
      dispatch(changeEditing(null));
      if (date?.isValid()) {
        setValue(date);
        updateAnnotation(
          annotationId,
          date.format(dateFormat),
          entityType,
          true,
          isTableMode ? true : entityType.multipleGroupBlocks,
        );
      }
    },
    [
      dispatch,
      annotationId,
      updateAnnotation,
      entityType,
      dateFormat,
      isTableMode,
    ],
  );

  const handleEnter = useCallback(
    (event?: SyntheticEvent) => {
      event?.stopPropagation();
      dispatch(changeEditing(null));
      if (!isCancelled.current) {
        updateAnnotation(
          annotationId,
          value.format(dateFormat),
          entityType,
          true,
          isTableMode,
        );
      }
    },
    [
      dispatch,
      annotationId,
      updateAnnotation,
      value,
      dateFormat,
      entityType,
      isTableMode,
    ],
  );

  const handleCancel = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      isCancelled.current = true;
      dispatch(changeEditing(null));
    },
    [dispatch],
  );

  const handleClick = useCallback((e: SyntheticEvent) => {
    console.log(e);
    e.stopPropagation();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", (event) => handleKeyEvent(event));
    return () => {
      document.removeEventListener("keydown", (event) => handleKeyEvent(event));
    };
  }, [handleKeyEvent]);

  return {
    value,
    setValue,
    dateFormat,
    handleChange,
    handleEnter,
    handleCancel,
    handleClick,
    defaultValue,
  };
};

export default useEditEntityDate;
